<script lang="ts">
  import type { FacetValue } from "../facet-value";
  import type { Facet, FacetOptions } from "./type.ts";
  import { List } from "../facet-value-group";

  import type { ClassValue } from "svelte/elements";
  import {
    addNameAndFeatureTracking,
    isNba,
    isNotInDenseMode,
    isNotPopular,
    isNotSelected,
    isPopular,
    isSelected,
  } from "./facetValues.ts";
  import { featureTrackingAttributes } from "../tracking";
  import SearchBox from "../input/SearchBox.svelte";
  import { setFilterTitleMode } from "../filter/filterTitleHack.ts";
  import type { FacetValueStatus } from "../facet-value/type.ts";
  import { updateLegacyLabel } from "../tracking/FilterTracking.ts";
  import { updateFeatureTrackingAttribute } from "../tracking/FeatureTracking.ts";
  import { isInDenseMode } from "./facetValues.js";
  import { activeConfiguration } from "../filter/stores.svelte.ts";
  import type { OcExpanderV1Events } from "@otto-ec/otto-components/expander";
  import { preselected } from "./preselection.svelte.ts";
  import { ephemeralFirstLevelValues } from "./EphemeralFirstLevelValues.svelte.ts";
  import { facetSettings } from "./settings.svelte.ts";
  import { initToggles, isActive, isNotActive } from "../../toggle/Toggle.ts";

  interface Props extends Facet, FacetOptions {
    class?: ClassValue;
    scrollable?: boolean;
  }

  let {
    id,
    name,
    title,
    featureTracking,
    visibility,
    values = [],
    contentSnippet = defaultList, /*                     */
    scrollable = true,
    searchable = false,
    searchboxFacetCountThreshold = 0,
    filterTitleMode = "onlyTitle",
    mode = "default",
    selectionType = "checkbox",
    showTitle = false,
    hasNbaValues = false,
    parentId = "",
    filterId,
    denseModeValuesLimit = 15,
    facetLocation,
    showPopularValues,
    ...rest
  }: Props = $props();

  initToggles();

  const showSearchbox =
    searchable && (searchboxFacetCountThreshold === 0 || values.length >= searchboxFacetCountThreshold);
  const facetId = `facet_${id}`;

  /*                                                                                                */
  /*                                        */
  /*                                                                           */
  /*                                                                                                                       */
  /*                                                                                    */
  /*                                                                                */
  const isThisFacetActive = $derived(
    activeConfiguration.openFilterIds === null ||
      ((mode === "default" || mode === "sheetFirstLevel") &&
        (filterId == null || activeConfiguration.openFilterIds?.has(filterId))) ||
      (mode === "sheetSecondLevel" && activeConfiguration.activeFilterId === filterId),
  );

  const shouldComputeFullLists = $derived(isThisFacetActive);

  const isMobile = $derived(mode === "sheetFirstLevel" || mode === "sheetSecondLevel");

  /*                                                                                       */
  let lastComputedUnselectedValues: FacetValue[] = [];
  let lastComputedUnselectedNonPopularValues: FacetValue[] = [];
  let lastComputedUnselectedDenseModeValues: FacetValue[] = [];
  let lastComputedUnselectedExpandedModeValues: FacetValue[] = [];
  let lastComputedDesktopPopularValues: FacetValue[] = [];
  let lastComputedUnselectedRestDenseModeValues: FacetValue[] = [];
  let lastComputedUnselectedRestExpandedModeValues: FacetValue[] = [];
  let lastComputedDesktopUnselectedNonPopularValues: FacetValue[] = [];

  const selectedValues = $derived(
    values.filter(isSelected).map(addNameAndFeatureTracking(facetId, name, 1, mode, facetLocation)),
  );

  const unselectedValues: FacetValue[] = $derived.by(() => {
    if (shouldComputeFullLists) {
      lastComputedUnselectedValues = values
        .filter(isNotSelected)
        .map(addNameAndFeatureTracking(facetId, name, selectedValues.length + 1, mode, facetLocation))
        .filter((value: FacetValue) => !showSearchbox || searchTerm === "" || filtered.includes(value.title))
        .map((value: FacetValue) => ({ ...value, highlighted: searchTerm }));
    }
    return lastComputedUnselectedValues;
  });

  /*                                           */
  const popularValues = $derived(
    values
      .filter(isPopular)
      .map(addNameAndFeatureTracking(facetId, name, selectedValues.length + 1, mode, facetLocation))
      /*                                                          */
      /*                                                                                                               */
      .map((value: FacetValue, index: number) => {
        updateFeatureTrackingAttribute(value, "status", value.checked ? "selected" : "visible");
        updateFeatureTrackingAttribute(value, "position", `${index + 1}`);
        return value;
      }),
  );

  const popularValuesCount = $derived(popularValues.length);

  /*                                                                                                    */
  const hidden = visibility === "mayBeRemoved";

  let filtered: string[] = $state([]);
  let searchTerm: string = $state("");
  let searchboxElement: HTMLElement | null = $state(null);

  /*                                                            */
  const unselectedPopularValues = $derived(
    values
      .filter(isPopular)
      .filter(isNotSelected)
      .map(addNameAndFeatureTracking(facetId, name, selectedValues.length + 1, mode, facetLocation)),
  );

  /*                                                      */
  const unselectedNonPopularNbas = $derived.by(() => {
    if (isActive("HEUREKA_2398_DO_NOT_RENDER_UNNEEDED_FACET_VALUES_IN_FIRST_LEVEL")) {
      return values
        .filter(
          (value, index, array) => isNba(value, index, array) || (name === "verkaeufer" && value.value === "otto"),
        )
        .filter(isNotSelected)
        .filter(isNotPopular)
        .map(addNameAndFeatureTracking(facetId, name))
        .map((value: FacetValue, index: number) => updateFeatureTrackingAttribute(value, "position", `${index + 1}`));
    } else if (!shouldComputeFullLists) {
      return values
        .filter(isNba)
        .filter(isNotSelected)
        .filter(isNotPopular)
        .map(addNameAndFeatureTracking(facetId, name));
    }
    return [];
  });

  const unselectedNonPopularValues = $derived.by(() => {
    if (shouldComputeFullLists) {
      lastComputedUnselectedNonPopularValues = values
        .filter(isNotPopular)
        .filter(isNotSelected)
        .map(
          addNameAndFeatureTracking(
            facetId,
            name,
            selectedValues.length + unselectedPopularValues.length + 1,
            mode,
            facetLocation,
          ),
        );
    }
    return lastComputedUnselectedNonPopularValues;
  });

  /*                                  */
  /*     */
  const unselectedDenseModeValues = $derived.by(() => {
    if (shouldComputeFullLists) {
      const denseValues = values.filter(isNotSelected).filter(isInDenseMode);

      const expandedValues = values.filter(isNotSelected).filter(isNotInDenseMode);

      /*                                                                         */
      const amountOfValuesToAdd = denseModeValuesLimit - denseValues.length - selectedValues.length;
      const combinedValues = denseValues
        .concat(expandedValues.slice(0, amountOfValuesToAdd))
        .slice(0, denseModeValuesLimit);

      lastComputedUnselectedDenseModeValues = combinedValues.map(
        addNameAndFeatureTracking(facetId, name, selectedValues.length + 1, mode, facetLocation),
      );
    }
    return lastComputedUnselectedDenseModeValues;
  });

  const unselectedExpandedModeValues = $derived.by(() => {
    if (shouldComputeFullLists) {
      const denseCount = values.filter(isNotSelected).filter(isInDenseMode).length;
      const amountOfAddedValues = denseModeValuesLimit - denseCount - selectedValues.length;

      lastComputedUnselectedExpandedModeValues = values
        .filter(isNotInDenseMode)
        .slice(amountOfAddedValues) /*                                              */
        .map(
          addNameAndFeatureTracking(
            facetId,
            name,
            unselectedDenseModeValues.length + selectedValues.length + 1,
            mode,
            facetLocation,
          ),
        )
        .filter((value: FacetValue) => !showSearchbox || searchTerm === "" || filtered.includes(value.title))
        .map((value: FacetValue) => ({ ...value, highlighted: searchTerm }));
    }
    return lastComputedUnselectedExpandedModeValues;
  });

  /*                                                                                                */
  /*                                                                                                     */
  /*                                                                                                    */
  /*                                                                                                */
  let lastComputedHasExpandedValues = false;
  const hasExpandedValues = $derived.by(() => {
    if (shouldComputeFullLists) {
      const denseCount = values.filter(isNotSelected).filter(isInDenseMode).length;
      const amountOfAddedValues = denseModeValuesLimit - denseCount - selectedValues.length;
      lastComputedHasExpandedValues = values.filter(isNotInDenseMode).length - amountOfAddedValues > 0;
    }
    return lastComputedHasExpandedValues;
  });

  const isShortenValueList = $derived(
    selectionType === "checkbox" &&
      selectedValues.length < denseModeValuesLimit &&
      unselectedDenseModeValues.length > 0,
  );

  /*      */
  const desktopPopularValues = $derived.by(() => {
    if (shouldComputeFullLists) {
      lastComputedDesktopPopularValues = values
        .filter(isNotSelected)
        .filter(isInDenseMode)
        .filter(isPopular)
        .slice(0, 5)
        .map(addNameAndFeatureTracking(facetId, name, selectedValues.length + 1, mode, facetLocation));
    }
    return lastComputedDesktopPopularValues;
  });

  const unselectedRestDenseModeValues = $derived.by(() => {
    if (shouldComputeFullLists) {
      lastComputedUnselectedRestDenseModeValues = values
        .filter(isNotSelected)
        .filter(isInDenseMode)
        .slice(desktopPopularValues.length, unselectedDenseModeValues.length)
        .map(
          addNameAndFeatureTracking(
            facetId,
            name,
            selectedValues.length + desktopPopularValues.length + 1,
            mode,
            facetLocation,
          ),
        );
    }
    return lastComputedUnselectedRestDenseModeValues;
  });

  const unselectedRestExpandedModeValues = $derived.by(() => {
    if (shouldComputeFullLists) {
      lastComputedUnselectedRestExpandedModeValues = values
        .filter(isNotSelected)
        .filter(isNotInDenseMode)
        .map(
          addNameAndFeatureTracking(
            facetId,
            name,
            selectedValues.length + unselectedRestDenseModeValues.length + 1,
            mode,
            facetLocation,
          ),
        );
    }
    return lastComputedUnselectedRestExpandedModeValues;
  });

  const desktopUnselectedNonPopularValues = $derived.by(() => {
    if (shouldComputeFullLists) {
      lastComputedDesktopUnselectedNonPopularValues = unselectedRestDenseModeValues.concat(
        unselectedRestExpandedModeValues,
      );
    }
    return lastComputedDesktopUnselectedNonPopularValues;
  });

  /*                                                                        */
  /*                                                               */
  function onchange({ target }: Event) {
    if (target instanceof HTMLInputElement && target.id) {
      $preselected[target.id] = target.checked;
    }
  }

  /*                      */
  let expanderElement: HTMLElement | null = $state(null);
  let isExpanderOpen: boolean = $state(false);

  /*                                                                                                      */
  /*                                                                                                     */
  /*                                                                                                      */
  /*                                                                                                    */
  /*                                                                                                      */
  /*                                                                                                   */
  /*                                                                                                      */
  /*                                                      */
  let expanderContentReady: boolean = $derived(!facetSettings.loadNotShownValuesAsynchronously || isExpanderOpen);

  /*                                                                                                */
  const sortedPopularValues = $derived.by(() => {
    const savedOrder = filterId ? ephemeralFirstLevelValues.values[filterId] : undefined;
    if (!savedOrder || savedOrder.length === 0) return popularValues;
    const sorted = [...popularValues].sort((a, b) => {
      const ai = savedOrder.indexOf(a.value);
      const bi = savedOrder.indexOf(b.value);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
    /*                                                                       */
    sorted.forEach((value, index) => {
      updateFeatureTrackingAttribute(value, "position", `${index + 1}`);
    });
    return sorted;
  });

  $effect(() => {
    expanderElement?.addEventListener("oc-property-change", expanderListener);
  });

  const expanderListener = (event: OcExpanderV1Events["oc-property-change"]) => {
    if (event.detail.expanded) {
      isExpanderOpen = event.detail.expanded.current === true;
    }
  };

  /*                               */
  $effect(() => {
    if (mode) {
      searchTerm = "";
    }
  });

  $effect(() => {
    const closestFilterForm = searchboxElement?.closest("form");
    if (closestFilterForm) {
      updateLegacyLabel(closestFilterForm, "san_FacetSearchTerm", searchTerm === "" ? "none" : searchTerm);
      updateLegacyLabel(closestFilterForm, "san_FacetSearchType", name);
    }
  });

  $effect(() => {
    if (showPopularValues) {
      /*                                                                    */
      /*                                                             */
      if (filterTitleMode === "onlyPopularValuesAndTitle" && popularValuesCount < 3) {
        /*                                                                                 */
        /*                                                                  */
        /*                                                                                   */
        if (selectedValues.filter(isNotPopular).length > 0) {
          setFilterTitleMode(parentId, "valuesAndTitle");
        } else {
          setFilterTitleMode(parentId, "onlyTitle");
        }
      }
    }
  });

  const trackingAttributes = $derived(featureTrackingAttributes(featureTracking));
</script>

{#snippet defaultList(values: FacetValue[], valueStatus: FacetValueStatus)}
  <List {values} {valueStatus}></List>
{/snippet}

<fieldset
  id={facetId}
  {name}
  class={["hc_facet", rest.class]}
  data-facet-visibility={visibility}
  data-has-nba-values={hasNbaValues}
  {...trackingAttributes}
  {hidden}
>
  {#if showTitle}
    <legend>
      <span>{title}</span>
    </legend>
  {/if}

  {#if mode === "sheetFirstLevel"}
    {#if filterTitleMode === "full"}
      {@render contentSnippet(
        values.map(addNameAndFeatureTracking(facetId, name, selectedValues.length + 1, mode, facetLocation)),
        "unselected",
      )}
    {:else}
      {#if filterTitleMode === "onlyTitle"}
        {@render contentSnippet(selectedValues.filter(isNotPopular), "selected")}
      {/if}
      {#if isNotActive("HEUREKA_2398_DO_NOT_RENDER_UNNEEDED_FACET_VALUES_IN_FIRST_LEVEL")}
        {@render contentSnippet(
          unselectedNonPopularValues.map(
            addNameAndFeatureTracking(
              facetId,
              name,
              selectedValues.length + popularValues.length + 1,
              mode,
              facetLocation,
            ),
          ),
          "unselected",
        )}
      {/if}
      {#if popularValues.length > 0}
        <div class="hc_facet__scrollBox">
          {#if filterTitleMode === "onlyPopularValuesAndTitle"}
            {@render contentSnippet(selectedValues.filter(isNotPopular), "selected")}
            {#if filterId && ephemeralFirstLevelValues.values[filterId]?.length > 0}
              {@render contentSnippet(sortedPopularValues, "unselectedPopular")}
            {:else}
              {@render contentSnippet(selectedValues.filter(isPopular), "selected")}
              {@render contentSnippet(unselectedPopularValues, "unselectedPopular")}
            {/if}
          {/if}
        </div>
      {/if}
    {/if}
    {#if isActive("HEUREKA_2398_DO_NOT_RENDER_UNNEEDED_FACET_VALUES_IN_FIRST_LEVEL")}
      <div style="display: none">
        {@render contentSnippet(unselectedNonPopularNbas, "unselected")}
      </div>
    {/if}
  {:else if mode === "sheetSecondLevel" && !isShortenValueList}
    <!-- Second Level Sheet non-checkbox case -->
    {@render contentSnippet(selectedValues, "selected")}
    {#if showSearchbox}
      <div
        class="hc_searchbox_wrapper"
        data-ts-labels={JSON.stringify({ san_FacetSearchTerm: searchTerm, san_FacetSearchType: name })}
        data-searchable-facet-input="#searchBox_facet_{id}"
        bind:this={searchboxElement}
      >
        <!-- re-render searchbox on mode change -->
        {#key mode}
          <SearchBox
            label="Nach {title} suchen"
            emptyMessage="Nichts gefunden – versuch’ einen anderen Begriff"
            items={unselectedNonPopularValues.concat(unselectedPopularValues).map((value: FacetValue) => value.title)}
            bind:value={searchTerm}
            bind:filtered
          />
        {/key}
      </div>
    {/if}
    <div
      class={[
        "hc_facet__scrollBox",
        { "hc_facet__scrollBox--scrollable": scrollable, "hc_facet__scrollBox--mobile": isMobile },
      ]}
      {onchange}
    >
      {@render contentSnippet(unselectedValues, "unselected")}
    </div>
  {:else if isShortenValueList && mode === "sheetSecondLevel"}
    <!-- Second Level Sheet checkbox case -->
    {@render contentSnippet(selectedValues, "selected")}
    {#if showSearchbox}
      <div
        class="hc_searchbox_wrapper"
        data-ts-labels={JSON.stringify({ san_FacetSearchTerm: searchTerm, san_FacetSearchType: name })}
        data-searchable-facet-input="#searchBox_facet_{id}"
        bind:this={searchboxElement}
      >
        <!-- re-render searchbox on mode change -->
        {#key mode}
          <SearchBox
            label="Nach {title} suchen"
            emptyMessage="Nichts gefunden – versuch’ einen anderen Begriff"
            items={unselectedNonPopularValues.concat(unselectedPopularValues).map((value: FacetValue) => value.title)}
            bind:value={searchTerm}
            bind:filtered
          />
        {/key}
      </div>
    {/if}
    {#if unselectedDenseModeValues.length + selectedValues.length >= denseModeValuesLimit}
      <div
        class={[
          "hc_facet__scrollBox",
          { "hc_facet__scrollBox--scrollable": scrollable, "hc_facet__scrollBox--mobile": isMobile },
        ]}
        {onchange}
      >
        {#if unselectedDenseModeValues.length > 0}
          {@render contentSnippet(
            unselectedDenseModeValues
              .filter((value: FacetValue) => !showSearchbox || searchTerm === "" || filtered.includes(value.title))
              .map((value: FacetValue) => ({ ...value, highlighted: searchTerm })),
            "unselectedPopular",
          )}
        {/if}
        <!-- Mehr anzeigen -->
        {#if hasExpandedValues}
          {#if searchTerm === ""}
            <oc-expander-v1
              data-tr-v1-oc-expander-open={JSON.stringify({
                san_FilterFoldoutType: filterId,
                san_FilterFoldoutActivity: "show_more_values",
              })}
              data-tr-v1-oc-expander-close={JSON.stringify({
                san_FilterFoldoutType: filterId,
                san_FilterFoldoutActivity: "show_less_values",
              })}
              class="hc_facetValueListExpander tr-v1"
              variant="text"
              hide-fade={false}
              cutoff-height={0}
              oc-aria-label="Weitere Werte"
              expanded={isExpanderOpen}
              bind:this={expanderElement}
            >
              {#if expanderContentReady || isExpanderOpen}
                {@render contentSnippet(unselectedExpandedModeValues, "unselected")}
              {:else}
                <!--
                  oc-expander-v1 measures the slotted content's clientHeight to decide whether to show its
                  "Mehr anzeigen" toggle at all (its collapsed box is always visually zero-height via
                  cutoff-height={0}, so this has no visual effect). A lightweight placeholder keeps that
                  detection working without mounting the full (potentially hundreds of items) value list
                  until the user actually opens it.
                -->
                <div style="height: 1px;" aria-hidden="true"></div>
              {/if}
            </oc-expander-v1>
          {:else}
            {@render contentSnippet(unselectedExpandedModeValues, "unselected")}
          {/if}
        {/if}
      </div>
    {:else}
      <div
        class={[
          "hc_facet__scrollBox",
          { "hc_facet__scrollBox--scrollable": scrollable, "hc_facet__scrollBox--mobile": isMobile },
        ]}
        {onchange}
      >
        {@render contentSnippet(unselectedValues, "unselected")}
      </div>
    {/if}
  {:else if isShortenValueList && mode === "default"}
    <!--    Desktop Mode -->
    {@render contentSnippet(selectedValues, "selected")}
    {#if showSearchbox}
      <div
        class="hc_searchbox_wrapper"
        data-ts-labels={JSON.stringify({ san_FacetSearchTerm: searchTerm, san_FacetSearchType: name })}
        data-searchable-facet-input="#searchBox_facet_{id}"
        bind:this={searchboxElement}
      >
        <!-- re-render searchbox on mode change -->
        {#key mode}
          <SearchBox
            label="Nach {title} suchen"
            emptyMessage="Nichts gefunden – versuch’ einen anderen Begriff"
            items={unselectedNonPopularValues.concat(unselectedPopularValues).map((value: FacetValue) => value.title)}
            bind:value={searchTerm}
            bind:filtered
          />
        {/key}
      </div>
    {/if}
    {#if popularValues.length > 0}
      <div
        class={[
          "hc_facet__scrollBox",
          { "hc_facet__scrollBox--scrollable": scrollable, "hc_facet__scrollBox--mobile": isMobile },
        ]}
      >
        {#if searchTerm === ""}
          {#if desktopPopularValues.length > 0}
            <span class="hc_facet__section_divider">BELIEBT</span>
            {@render contentSnippet(desktopPopularValues, "unselectedPopular")}
          {/if}
          {#if desktopUnselectedNonPopularValues.length > 0}
            <span class="hc_facet__section_divider">WEITERE</span>
            {@render contentSnippet(desktopUnselectedNonPopularValues, "unselected")}
          {/if}
        {:else}
          {@render contentSnippet(unselectedValues, "unselected")}
        {/if}
      </div>
    {:else}
      <div
        class={[
          "hc_facet__scrollBox",
          { "hc_facet__scrollBox--scrollable": scrollable, "hc_facet__scrollBox--mobile": isMobile },
        ]}
      >
        {@render contentSnippet(unselectedValues, "unselected")}
      </div>
    {/if}
  {:else}
    <!--    Desktop Mode -->
    {@render contentSnippet(selectedValues, "selected")}
    {#if showSearchbox}
      <div
        class="hc_searchbox_wrapper"
        data-ts-labels={JSON.stringify({ san_FacetSearchTerm: searchTerm, san_FacetSearchType: name })}
        data-searchable-facet-input="#searchBox_facet_{id}"
        bind:this={searchboxElement}
      >
        <!-- re-render searchbox on mode change -->
        {#key mode}
          <SearchBox
            label="Nach {title} suchen"
            emptyMessage="Nichts gefunden – versuch’ einen anderen Begriff"
            items={unselectedNonPopularValues.concat(unselectedPopularValues).map((value: FacetValue) => value.title)}
            bind:value={searchTerm}
            bind:filtered
          />
        {/key}
      </div>
    {/if}
    {#if popularValues.length > 0}
      <div
        class={[
          "hc_facet__scrollBox",
          { "hc_facet__scrollBox--scrollable": scrollable, "hc_facet__scrollBox--mobile": isMobile },
        ]}
      >
        {#if searchTerm === ""}
          {#if unselectedPopularValues.length > 0}
            <span class="hc_facet__section_divider">BELIEBT</span>
            {@render contentSnippet(unselectedPopularValues, "unselectedPopular")}
          {/if}
          {#if unselectedNonPopularValues.length > 0}
            <span class="hc_facet__section_divider">WEITERE</span>
            {@render contentSnippet(unselectedNonPopularValues, "unselected")}
          {/if}
        {:else}
          {@render contentSnippet(unselectedValues, "unselected")}
        {/if}
      </div>
    {:else}
      <div
        class={[
          "hc_facet__scrollBox",
          { "hc_facet__scrollBox--scrollable": scrollable, "hc_facet__scrollBox--mobile": isMobile },
        ]}
      >
        {@render contentSnippet(unselectedValues, "unselected")}
      </div>
    {/if}
  {/if}
  {#if isNotActive("HEUREKA_2398_DO_NOT_RENDER_UNNEEDED_FACET_VALUES_IN_FIRST_LEVEL")}
    <div style="display: none">
      {@render contentSnippet(unselectedNonPopularNbas, "unselected")}
    </div>
  {/if}
</fieldset>

<style lang="scss">
  @use "@otto-ec/otto-components-utils/scss/mixins";
  fieldset {
    min-width: 0;

    span {
      font: var(--oc-semantic-font-copy-100);
      font-weight: 700;
    }
  }

  .hc_searchbox_wrapper {
    margin: 6px 2px 16px 2px;
  }

  .hc_facet__section_divider {
    font-family: "OttoSans", Arial, Helvetica, sans-serif;
    color: #777;
    font-size: 0.75rem;
    line-height: 22px;
    padding: 4px 0;
    display: block;
    font-weight: normal;

    &:not(:first-child) {
      margin-top: 4px;
    }
  }

  .hc_facet__scrollBox {
    @include mixins.breakpoint-from-s($to: "l") {
      display: flex;
      gap: var(--oc-semantic-spacing-50);
      flex-wrap: wrap;
      :global(.hc_facetValueGroup__container),
      :global(.hc_facetValueGroup__list) {
        display: contents;
      }
    }

    &--scrollable {
      margin-left: calc(-2 * var(--oc-semantic-focus-outline-offset));
      padding-left: calc(2 * var(--oc-semantic-focus-outline-offset));

      &:not(.hc_facet__scrollBox--mobile) {
        overflow-y: auto;
      }
    }
  }

  :global(.heureka_filterAccordion__content) {
    .hc_facet__scrollBox {
      max-height: var(--scroll-area-height, 365px);
    }
  }

  :global(.heureka_filterTitle__values) {
    .hc_facet__scrollBox--scrollable {
      scrollbar-width: none;
    }
  }
</style>
